import { useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';
// components
import Header from './Header';
// styles
import { palette } from '../styles';

interface Props {
  animated: 'OPEN' | 'CLOSE' | 'NONE';
  duration: number;
  onClose: Function;
  children: ReactNode;
}

/**
 * Bottom Sheet
 * @param props
 * @param props.animated 애니메이션 상태
 * @param props.duration 애니메이션 duration 시간(단위: second)
 * @param props.onClose
 */
const Sheet = ({ animated, duration, onClose, children }: Props) => {
  const [isClose, setIsClose] = useState(false);

  const sheet = useRef(null);
  const startY = useRef(0);
  const prevY = useRef(0);
  const currentY = useRef(0);
  const direction = useRef<'UP' | 'DOWN'>('UP');

  const onPreventClose = e => e.stopPropagation();

  const onTouchMove = e => {
    if (e.cancelable) {
      e.preventDefault();
    }

    prevY.current = currentY.current;
    currentY.current = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;

    const offsetDiff = currentY.current - startY.current;
    if (offsetDiff > 0) {
      sheet.current.style.transition = `transform 0s`;
      sheet.current.style.transform = `translateY(${offsetDiff}px)`;
    }

    const directionDiff = currentY.current - prevY.current;
    if (directionDiff > 0) {
      direction.current = 'DOWN';
    } else {
      direction.current = 'UP';
    }
  };

  const onTouchStart = useCallback(e => {
    startY.current = currentY.current = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
    e.currentTarget.addEventListener('touchmove', onTouchMove);
    e.currentTarget.addEventListener('mousemove', onTouchMove);
  }, []);

  const onTouchEnd = useCallback(e => {
    e.currentTarget.removeEventListener('touchmove', onTouchMove);
    e.currentTarget.removeEventListener('mousemove', onTouchMove);

    const { height: sheetHeight } = sheet.current.getBoundingClientRect();
    const offsetDiff = currentY.current - startY.current;

    sheet.current.style.transition = '';
    if (offsetDiff >= sheetHeight / 3 && direction.current === 'DOWN') {
      sheet.current.style.transform = `translateY(100%)`;
      setIsClose(true);
    } else {
      sheet.current.style.transform = `translateY(0px)`;
    }
  }, []);

  useEffect(() => {
    const sheetRef = sheet.current;
    sheetRef.addEventListener('touchstart', onTouchStart);
    sheetRef.addEventListener('touchend', onTouchEnd);
    sheetRef.addEventListener('mousedown', onTouchStart);
    sheetRef.addEventListener('mouseup', onTouchEnd);
    return () => {
      sheetRef.removeEventListener('touchstart', onTouchStart);
      sheetRef.removeEventListener('touchend', onTouchEnd);
      sheetRef.removeEventListener('mousedown', onTouchStart);
      sheetRef.removeEventListener('mouseup', onTouchEnd);
    };
  }, [onTouchStart, onTouchEnd]);

  useEffect(() => {
    let timer = null;

    if (isClose) {
      timer = window.setTimeout(() => onClose(), duration);
    }

    return () => window.clearTimeout(timer);
  }, [isClose, onClose, duration]);

  return (
    <Wrapper onMouseDown={onPreventClose} animated={animated} duration={duration} ref={sheet}>
      <Header />
      {children}
    </Wrapper>
  );
};

const AnimeSlideInBottom = keyframes`
	from { transform: translateY(100%); }
	to { transform: translateY(0%); }
`;

const AnimeSlideOutBottom = keyframes`
	from { transform: translateY(0%); }
	to { transform: translateY(100%); }
`;

const Wrapper = styled.dialog<{
  animated: 'OPEN' | 'CLOSE' | 'NONE';
  duration: number;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 0;
  padding-bottom: calc(constant(safe-area-inset-bottom));
  padding-bottom: calc(env(safe-area-inset-bottom));
  border-radius: 16px 16px 0 0;
  background-color: ${palette.system.BNB};
  transition: ${({ duration }) => `transform ${duration}ms ease`};
  ${({ animated, duration }) => {
    if (animated === 'OPEN') {
      return css`
        animation: ${AnimeSlideInBottom} ${duration}ms;
      `;
    } else if (animated === 'CLOSE') {
      return css`
        animation: ${AnimeSlideOutBottom} ${duration}ms;
      `;
    } else {
      return '';
    }
  }}
  animation-fill-mode: forwards;
`;

export default Sheet;
